import { Button, Input, Label } from '@monksflow/monks-ui';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  isReservedAttributeName,
  isValidAttributeName,
  type CustomAttribute,
} from '../../model/customAttributes';

interface CustomAttributesEditorProps {
  attributes: CustomAttribute[];
  onChange: (attributes: CustomAttribute[]) => void;
}

type AttributeNameIssue = 'invalid' | 'reserved' | 'duplicate' | 'required';

/**
 * Why `name` cannot be used, or null when it is fine. Returns a code rather than copy so the
 * translator stays in the component, where `t` keeps its compile-time key checking.
 *
 * Emptiness is judged per ROW, not per field. A wholly untouched row stays quiet — a freshly added
 * blank row should not shout before anything has been typed — but a row carrying a VALUE with no
 * name is half-filled, and saying nothing there is what left Save disabled with no cause on screen
 * and the typed value dropped on submit.
 */
function attributeNameIssue(
  name: string,
  index: number,
  attributes: CustomAttribute[],
): AttributeNameIssue | null {
  const trimmed = name.trim();
  if (!trimmed) return attributes[index]?.value ? 'required' : null;
  if (!isValidAttributeName(trimmed)) return 'invalid';
  if (isReservedAttributeName(trimmed)) return 'reserved';
  const isDuplicate = attributes.some(
    (other, otherIndex) =>
      otherIndex !== index && other.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  return isDuplicate ? 'duplicate' : null;
}

export function hasAttributeErrors(attributes: CustomAttribute[]): boolean {
  return attributes.some((attribute, index) => {
    const trimmed = attribute.name.trim();
    // A wholly blank row is unfilled, not invalid — the modal drops it on submit. A row with a value
    // but no name blocks, and `attributeNameIssue` says why, so the block is never unexplained.
    if (!trimmed) return Boolean(attribute.value);
    if (!isValidAttributeName(trimmed) || isReservedAttributeName(trimmed)) return true;
    return attributes.some(
      (other, otherIndex) =>
        otherIndex !== index && other.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
  });
}

/**
 * Name/value repeater for attributes a client needs on the published element. The value is
 * optional — an empty one is written as `attr=""`, which is the valueless-marker case.
 */
export function CustomAttributesEditor({ attributes, onChange }: CustomAttributesEditorProps) {
  const { t } = useTranslation('admin');

  const update = (index: number, patch: Partial<CustomAttribute>) => {
    onChange(
      attributes.map((attribute, i) => (i === index ? { ...attribute, ...patch } : attribute)),
    );
  };

  const issueMessage = (issue: AttributeNameIssue): string => {
    if (issue === 'invalid') return t('templatesConfig.configureElement.invalidAttributeName');
    if (issue === 'reserved') return t('templatesConfig.configureElement.reservedAttributeName');
    if (issue === 'required') return t('templatesConfig.configureElement.attributeNameRequired');
    return t('templatesConfig.configureElement.duplicateAttributeName');
  };

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('templatesConfig.configureElement.customAttributes')}</Label>
      {attributes.map((attribute, index) => {
        const issue = attributeNameIssue(attribute.name, index, attributes);
        const error = issue ? issueMessage(issue) : null;
        return (
          <div key={index} className="flex flex-col gap-1">
            <div className="flex items-start gap-2">
              <Input
                aria-label={t('templatesConfig.configureElement.attributeName')}
                placeholder={t('templatesConfig.configureElement.attributeNamePlaceholder')}
                value={attribute.name}
                onChange={(event) => update(index, { name: event.target.value })}
                aria-invalid={!!error || undefined}
                className="flex-1"
              />
              <Input
                aria-label={t('templatesConfig.configureElement.attributeValue')}
                placeholder={t('templatesConfig.configureElement.attributeValuePlaceholder')}
                value={attribute.value}
                onChange={(event) => update(index, { value: event.target.value })}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('templatesConfig.configureElement.removeAttribute')}
                onClick={() => onChange(attributes.filter((_, i) => i !== index))}
              >
                <Trash2Icon className="size-4" />
              </Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        );
      })}
      <Button
        variant="outline"
        size="sm"
        className="self-start"
        onClick={() => onChange([...attributes, { name: '', value: '' }])}
      >
        <PlusIcon className="size-4" />
        {t('templatesConfig.configureElement.addAttribute')}
      </Button>
    </div>
  );
}
